import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { TDMSNext, TDMSNextSDK } from '@tdms/tdms-next-api';

const getResponseStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }

  const { response } = error;

  if (
    typeof response !== 'object' ||
    response === null ||
    !('status' in response)
  ) {
    return undefined;
  }

  return typeof response.status === 'number' ? response.status : undefined;
};

export type TDMSNextClient = Omit<
  TDMSNext<unknown>,
  'instance' | 'request' | 'setSecurityData'
>;

export type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
  onForbidden?: () => void;
};

/**
 * TDMS API Client for interacting with the TDMS Next API.
 */
export class TDMSApi implements TDMSNextClient {
  get admin(): TDMSNextClient['admin'] {
    return this.getClient().admin;
  }

  get configs(): TDMSNextClient['configs'] {
    return this.getClient().configs;
  }

  get providers(): TDMSNextClient['providers'] {
    return this.getClient().providers;
  }

  get tenants(): TDMSNextClient['tenants'] {
    return this.getClient().tenants;
  }

  get users(): TDMSNextClient['users'] {
    return this.getClient().users;
  }

  get webhooks(): TDMSNextClient['webhooks'] {
    return this.getClient().webhooks;
  }

  private baseUrl?: string;
  private client: TDMSNextClient | null = null;
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private initializationPromise: Promise<void> | null = null;
  private readonly onForbidden?: () => void;

  /**
   * Creates an instance of TDMSApiClient.
   * @param {Options} options - The options for configuring the client.
   */
  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.onForbidden = options.onForbidden;
  }

  /**
   * Initializes the TDMS Next SDK client and sets up the API endpoints.
   */
  public async initialize(): Promise<void> {
    if (this.client) {
      return;
    }

    if (this.initializationPromise) {
      await this.initializationPromise;

      return;
    }

    this.initializationPromise = (async () => {
      const baseURL = await this.getBaseUrl();

      const sdk = TDMSNextSDK.configure({
        baseURL,
        securityWorker: async () => ({
          headers: {
            Authorization: `Bearer ${await this.getAccessToken()}`,
          },
        }),
      });

      if (this.onForbidden) {
        sdk.enablePlugin(axios => {
          axios.interceptors.response.use(
            response => response,
            (error: unknown) => {
              if (getResponseStatus(error) === 403) this.onForbidden?.();

              return Promise.reject(error);
            },
          );

          return axios;
        });
      }

      this.client = sdk.connect();
    })();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async getAccessToken(): Promise<string> {
    const identity = await this.identityApi.getCredentials();

    if (!identity?.token) {
      throw new Error('TDMSApi: No identity credentials available');
    }

    return identity.token;
  }

  private async getBaseUrl(): Promise<string> {
    if (!this.baseUrl) {
      this.baseUrl = `${await this.discoveryApi.getBaseUrl('tdms-next')}`;
    }

    return this.baseUrl;
  }

  private getClient(): TDMSNextClient {
    if (!this.client) {
      throw new Error('TDMSApi: SDK client not initialized');
    }

    return this.client;
  }
}

export const tdmsApiRef: ApiRef<TDMSApi> = createApiRef<TDMSApi>({
  id: 'plugin.tdms-next.api',
});
